/**
 * Optimized Icon Registry using direct SVG imports
 * 
 * Performance benefits:
 * - Tree-shaking: Only imported icons are bundled
 * - Build-time optimization: SVGs processed during build
 * - Better caching: Each SVG is a separate chunk
 * - Type safety: Import errors caught at build time
 * - Smaller bundle: No inline SVG strings
 */

// Import SVG components directly
import SatelliteIcon from 'src/assets/images/vector/sattelite.svg';
import MoonIcon from 'src/assets/images/vector/moon.svg';

// Import sidebar icons
import SidebarAgencyIcon from 'src/assets/images/vector/sidebar/agency.svg';
import SidebarCollapseIcon from 'src/assets/images/vector/sidebar/collapse.svg';
import SidebarDashboardIcon from 'src/assets/images/vector/sidebar/dashboard.svg';
import SidebarExpandIcon from 'src/assets/images/vector/sidebar/expand.svg';
import SidebarLiveMapIcon from 'src/components/library/icon/components/SidebarLiveMapIcon.jsx';
import SidebarRoutesIcon from 'src/assets/images/vector/sidebar/routes.svg';
import SidebarSignOutIcon from 'src/assets/images/vector/lyt_icons/logout.svg';
import SidebarThemeIcon from 'src/assets/images/vector/sidebar/theme.svg';
import SidebarUsersAndPermissionsIcon from 'src/assets/images/vector/sidebar/users-and-permissions.svg';
import SidebarLytTeamIcon from 'src/assets/images/vector/sidebar/lyt-team.svg';

// Import lyt_icons (includes toast icons and utility icons)
import ToastSuccessIcon from 'src/assets/images/vector/lyt_icons/check-circle-fill.svg';
import ToastErrorIcon from 'src/assets/images/vector/lyt_icons/cancel-fill.svg';
import ToastWarningIcon from 'src/assets/images/vector/lyt_icons/warning-fill.svg';
import ToastInfoIcon from 'src/assets/images/vector/lyt_icons/info-circle-fill.svg';
import CircleNotificationsFillIcon from 'src/assets/images/vector/lyt_icons/circle-notifications-fill.svg';
import CircleInformationFillIcon from 'src/assets/images/vector/lyt_icons/circle-information-fill.svg';
import CircleLogoIcon from 'src/assets/images/vector/lyt_icons/circle-logo.svg';
import SidebarTeamIcon from 'src/assets/images/vector/lyt_icons/team.svg';
import SidebarRequestsIcon from 'src/assets/images/vector/lyt_icons/requests.svg';
import SidebarCadAvlIcon from 'src/assets/images/vector/lyt_icons/cad-avl-settings.svg';
import SidebarAlertsIcon from 'src/assets/images/vector/lyt_icons/flag.svg';
import MoonFilledIcon from 'src/assets/images/vector/lyt_icons/moon-filled.svg';
import SunFilledIcon from 'src/assets/images/vector/lyt_icons/sun-filled.svg';
import MoreIcon from 'src/assets/images/vector/lyt_icons/More.svg';
import ChevronDownIcon from 'src/assets/images/vector/lyt_icons/chevron-down.svg';
import ZoomInIcon from 'src/assets/images/vector/lyt_icons/zoom-in.svg';
import ZoomOutIcon from 'src/assets/images/vector/lyt_icons/zoom-out.svg';
import SearchIcon from 'src/assets/images/vector/lyt_icons/search-magnifying.svg';
import LayersIcon from 'src/assets/images/vector/lyt_icons/layers.svg';
import HelpCircleIcon from 'src/assets/images/vector/lyt_icons/help-circle.svg';
import ArrowLeftIcon from 'src/assets/images/vector/lyt_icons/arrow-left.svg';

// User details icons
import PersonFillIcon from 'src/assets/images/vector/lyt_icons/person-fill.svg';
import StackedEmailFillIcon from 'src/assets/images/vector/lyt_icons/stacked-email-fill.svg';
import BriefcaseFillIcon from 'src/assets/images/vector/lyt_icons/briefcase-fill.svg';
import CalendarTodayFillIcon from 'src/assets/images/vector/lyt_icons/calendar-today-fill.svg';
import CommentFillIcon from 'src/assets/images/vector/lyt_icons/comment-fill.svg';

// Modal icons
import XIcon from 'src/assets/images/vector/lyt_icons/X.svg';

// Alert/Warning icons
import BrightnessAlertFillIcon from 'src/assets/images/vector/lyt_icons/brightness-alert-fill.svg';

// Account icon
import UserAccountIcon from 'src/assets/images/vector/lyt_icons/user-account.svg';
import VerticalDotsIcon from 'src/assets/images/vector/lyt_icons/vertical-dots.svg';

// Map center icons
import CenterAgencyIcon from 'src/assets/images/vector/lyt_icons/center-agency.svg';
import CenterVehicleIcon from 'src/assets/images/vector/lyt_icons/center-vehicle.svg';

import { ICON_NAMES } from 'src/components/library/icon/icon-constants';
import { HelpCircle } from 'react-feather';

// Note: colors import removed since we use defaults from OptimizedCustomIcon
// Import colors only when you need to override default theme colors for specific icons

/**
 * Registry of optimized custom icons
 * Each entry contains the SVG component and theme configuration
 */
export const OPTIMIZED_ICON_REGISTRY = {
    [ICON_NAMES.Satellite]: {
        component: SatelliteIcon,
        // Uses default theme colors from OptimizedCustomIcon
    },
    [ICON_NAMES.ZoomIn]: {
        component: ZoomInIcon,
    },
    [ICON_NAMES.ZoomOut]: {
        component: ZoomOutIcon,
    },
    [ICON_NAMES.Layers]: {
        component: LayersIcon,
    },
    [ICON_NAMES.ArrowLeft]: {
        component: ArrowLeftIcon,
    },
    [ICON_NAMES.HelpCircle]: {
        component: HelpCircleIcon,
    },
    [ICON_NAMES.Moon]: {
        component: MoonIcon,
        // Uses default theme colors from OptimizedCustomIcon
    },
    [ICON_NAMES.Search]: {
        component: SearchIcon,
    },
    [ICON_NAMES.CircleInformationFill]: {
        component: CircleInformationFillIcon,
    },
    // Sidebar icons - all use default theme colors
    [ICON_NAMES.SidebarAgency]: {
        component: SidebarAgencyIcon,
    },
    [ICON_NAMES.VerticalDots]: {
        component: VerticalDotsIcon,
    },
    [ICON_NAMES.SidebarCollapse]: {
        component: SidebarCollapseIcon,
    },
    [ICON_NAMES.SidebarDashboard]: {
        component: SidebarDashboardIcon,
    },
    [ICON_NAMES.SidebarExpand]: {
        component: SidebarExpandIcon,
    },
    [ICON_NAMES.SidebarLiveMap]: {
        component: SidebarLiveMapIcon,
    },
    [ICON_NAMES.SidebarRoutes]: {
        component: SidebarRoutesIcon,
    },
    [ICON_NAMES.SidebarSignOut]: {
        component: SidebarSignOutIcon,
    },
    [ICON_NAMES.SidebarTheme]: {
        component: SidebarThemeIcon,
    },
    [ICON_NAMES.SidebarUsersAndPermissions]: {
        component: SidebarUsersAndPermissionsIcon,
    },
    [ICON_NAMES.UserAccount]: {
        component: UserAccountIcon,
    },
    
    // Toast icons
    [ICON_NAMES.ToastSuccess]: {
        component: ToastSuccessIcon,
    },
    [ICON_NAMES.ToastError]: {
        component: ToastErrorIcon,
    },
    [ICON_NAMES.ToastWarning]: {
        component: ToastWarningIcon,
    },
    [ICON_NAMES.ToastInfo]: {
        component: ToastInfoIcon,
    },
    [ICON_NAMES.CircleNotificationsFill]: {
        component: CircleNotificationsFillIcon,
    },
    
    // Logo icons
    [ICON_NAMES.CircleLogo]: {
        component: CircleLogoIcon,
        preserveColors: true, // Keep original SVG colors for the logo
        useAsImage: true, // Complex SVG with masks - render as img for proper display
    },
    
    // Sidebar navigation icons (from lyt_icons)
    [ICON_NAMES.SidebarTeam]: {
        component: SidebarTeamIcon,
    },
    [ICON_NAMES.SidebarRequests]: {
        component: SidebarRequestsIcon,
    },
    [ICON_NAMES.SidebarCadAvl]: {
        component: SidebarCadAvlIcon,
    },
    [ICON_NAMES.SidebarAlerts]: {
        component: SidebarAlertsIcon,
    },
    [ICON_NAMES.SidebarLytTeam]: {
        component: SidebarLytTeamIcon,
        preserveColors: true,
    },
    
    // Theme icons
    [ICON_NAMES.MoonFilled]: {
        component: MoonFilledIcon,
    },
    [ICON_NAMES.SunFilled]: {
        component: SunFilledIcon,
    },
    
    // Action icons
    [ICON_NAMES.More]: {
        component: MoreIcon,
    },
    
    // Chevron icons
    [ICON_NAMES.ChevronDown]: {
        component: ChevronDownIcon,
    },
    
    // User details icons
    [ICON_NAMES.PersonFill]: {
        component: PersonFillIcon,
    },
    [ICON_NAMES.StackedEmailFill]: {
        component: StackedEmailFillIcon,
    },
    [ICON_NAMES.BriefcaseFill]: {
        component: BriefcaseFillIcon,
    },
    [ICON_NAMES.CalendarTodayFill]: {
        component: CalendarTodayFillIcon,
    },
    [ICON_NAMES.CommentFill]: {
        component: CommentFillIcon,
    },
    
    // Modal icons
    [ICON_NAMES.X]: {
        component: XIcon,
    },
    
    // Alert/Warning icons
    [ICON_NAMES.BrightnessAlertFill]: {
        component: BrightnessAlertFillIcon,
    },

    // Map Icons
    [ICON_NAMES.CenterAgency]: {
        component: CenterAgencyIcon,
    },
    [ICON_NAMES.CenterVehicle]: {
        component: CenterVehicleIcon,
    },
    [ICON_NAMES.CenterAgency]: {
        component: CenterAgencyIcon,
    },
    [ICON_NAMES.CenterVehicle]: {
        component: CenterVehicleIcon,
    },

    
    // Example of how to override default theme colors for specific icons:
    // [ICON_NAMES.SomeSpecialIcon]: {
    //     component: SomeSpecialIcon,
    //     lightThemeFill: colors.Primary,
    //     darkThemeFill: colors.Secondary,
    // },
};

/**
 * Get an optimized icon configuration by name
 * @param {string} name - Icon name
 * @returns {Object|null} Icon configuration or null if not found
 */
export const getOptimizedIcon = (name) => OPTIMIZED_ICON_REGISTRY[name];

/**
 * Get all available optimized icon names
 * @returns {string[]} Array of icon names
 */
export const getOptimizedIconNames = () => Object.keys(OPTIMIZED_ICON_REGISTRY);
